import React, { useCallback, useState } from "react";

import { MultipleLineChartComponent } from "../MultipleLine";
import { InspectableLineChartProps } from "./types";
import { readoutLinesOf, readoutTextOf, stepFor } from "./utils";
import { InspectableChart, Inspector, Readout, ReadoutDate } from "./styles";

export const InspectableLineChart: React.FC<InspectableLineChartProps> = ({
  inspectLabel,
  readoutDates: givenReadoutDates,
  readoutSeries,
  readoutFooter,
  readoutFooterText,
  readoutTestId,
  series,
  xLabels,
  ...chartProps
}) => {
  const readoutDates = givenReadoutDates ?? xLabels;
  const lastIndex = readoutDates.length - 1;
  const [inspected, setInspected] = useState({
    dates: readoutDates,
    index: lastIndex,
  });
  const inspectedIndex =
    inspected.dates === readoutDates ? inspected.index : lastIndex;
  const inspect = useCallback(
    (index: number) => setInspected({ dates: readoutDates, index }),
    [readoutDates],
  );

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const next = stepFor(event.key, inspectedIndex, lastIndex);
    if (next === null) {
      return;
    }
    event.preventDefault();
    inspect(next);
  };

  if (readoutDates.length === 0) {
    return null;
  }

  if (import.meta.env.DEV && readoutDates.length !== xLabels.length) {
    console.error(
      `InspectableLineChart: ${readoutDates.length} readout dates for ${xLabels.length} x labels`,
    );
  }

  const date = readoutDates[inspectedIndex];
  const lines = readoutLinesOf(readoutSeries ?? series, inspectedIndex);
  const readoutText = readoutTextOf(
    date,
    lines,
    readoutFooterText?.(inspectedIndex),
  );
  const footer = readoutFooter?.(inspectedIndex);

  return (
    <InspectableChart>
      <Inspector
        role="slider"
        tabIndex={0}
        aria-label={inspectLabel}
        aria-valuemin={0}
        aria-valuemax={lastIndex}
        aria-valuenow={inspectedIndex}
        aria-valuetext={readoutText}
        onKeyDown={handleKeyDown}
      >
        <MultipleLineChartComponent
          {...chartProps}
          series={series}
          xLabels={xLabels}
          highlightedIndex={inspectedIndex}
          onHighlightedIndexChange={inspect}
          hideTooltip
        />
      </Inspector>
      <div data-testid={readoutTestId}>
        <Readout aria-hidden="true">
          <ReadoutDate>{date}</ReadoutDate>
          {lines.map(({ label, value }) => (
            <span key={label}>
              {label} {value}
            </span>
          ))}
        </Readout>
        {footer}
      </div>
    </InspectableChart>
  );
};
